import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef,
  useMemo
} from 'react';
import { useRealtimeWebSocket } from '@utils/api/realtime/use-realtime-websocket.ts';
import { useManagedLiveStart } from './use-managed-live-start.ts';
import {
  RealtimeConfig,
  RealtimeConnectionState,
  RealtimeMessage,
  RealtimeStats,
  AudioSyncState
} from '@utils/types/editors/realtime.ts';

type RealtimeContextType = {
  connectionState: RealtimeConnectionState;
  currentPartial: string;
  partialHistory: string[];
  finals: RealtimeMessage[];
  stats: RealtimeStats;
  error: string | null;
  config: RealtimeConfig | null;
  audioSync: AudioSyncState;
  speakers: string[];
  connect: (config: RealtimeConfig) => Promise<void>;
  disconnect: () => void;
  sendAudioData: (data: ArrayBuffer) => void;
  clearPartials: () => void;
  clearFinals: () => void;
  clearAll: () => void;
  reset: () => boolean;
  startPending: () => boolean;
  setConfig: (config: RealtimeConfig) => void;
  clearStartError: () => void;
  updateAudioSync: (sync: Partial<AudioSyncState>) => void;
  updateFinalSegment: (index: number, newText: string) => void;
  updateSpeaker: (index: number, speaker: string) => void;
  addSpeaker: (speaker: string) => void;
};

const RealtimeContext = createContext<RealtimeContextType | undefined>(
  undefined
);

type RealtimeProviderProps = {
  children: ReactNode;
};

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({
  children
}) => {
  const [config, setConfig] = useState<RealtimeConfig | null>(null);
  const [audioSync, setAudioSync] = useState<AudioSyncState>({
    userOffset: 0,
    audioCurrentTime: 0,
    isPlaying: false
  });
  const [speakers, setSpeakers] = useState<string[]>([]);
  const [corrections, setCorrections] = useState<
    Record<number, { text?: string; speaker?: string }>
  >({});
  const managed = useManagedLiveStart();

  const {
    connectionState: legacyState,
    currentPartial,
    partialHistory,
    finals: originalFinals,
    stats,
    error: legacyError,
    connect: wsConnect,
    disconnect: wsDisconnect,
    sendAudioData,
    clearPartials,
    clearFinals: wsClearFinals,
    clearAll: wsClearAll,
    reset: wsReset
  } = useRealtimeWebSocket();

  const finals = useMemo(
    () =>
      originalFinals.map((result, index) =>
        result.is_final ? { ...result, ...corrections[index] } : result
      ),
    [corrections, originalFinals]
  );
  const connectionState = managed.starting ? 'connecting' : legacyState;
  const error = managed.error || legacyError;
  const clearFinals = useCallback(() => {
    setCorrections({});
    wsClearFinals();
  }, [wsClearFinals]);
  const clearAll = useCallback(() => {
    setCorrections({});
    wsClearAll();
  }, [wsClearAll]);
  const reset = useCallback(() => {
    if (!managed.reset()) return false;
    wsReset();
    setCorrections({});
    setSpeakers([]);
    setConfig(null);
    setAudioSync({ userOffset: 0, audioCurrentTime: 0, isPlaying: false });
    return true;
  }, [managed, wsReset]);

  const connect = useCallback(
    async (newConfig: RealtimeConfig) => {
      setConfig(newConfig);
      if (newConfig.audioSource === 'stream') {
        await managed.start(newConfig);
        return;
      }
      await wsConnect(newConfig);
    },
    [managed.start, wsConnect]
  );

  const disconnect = useCallback(() => {
    wsDisconnect();
    setAudioSync(prev => ({
      ...prev,
      isPlaying: false
    }));
  }, [wsDisconnect]);

  const updateAudioSyncRef = useRef((sync: Partial<AudioSyncState>) => {
    setAudioSync(prev => ({
      ...prev,
      ...sync
    }));
  });

  const updateAudioSync = useCallback((sync: Partial<AudioSyncState>) => {
    updateAudioSyncRef.current(sync);
  }, []);

  const updateFinalSegment = useCallback((index: number, newText: string) => {
    setCorrections(previous => ({
      ...previous,
      [index]: { ...previous[index], text: newText }
    }));
  }, []);

  const updateSpeaker = useCallback(
    (index: number, speaker: string) => {
      setCorrections(previous => ({
        ...previous,
        [index]: { ...previous[index], speaker }
      }));
      // Add speaker to list if not already present
      if (speaker && !speakers.includes(speaker)) {
        setSpeakers(prev => [...prev, speaker]);
      }
    },
    [speakers]
  );

  const addSpeaker = useCallback(
    (speaker: string) => {
      if (speaker && !speakers.includes(speaker)) {
        setSpeakers(prev => [...prev, speaker]);
      }
    },
    [speakers]
  );

  const value: RealtimeContextType = {
    clearStartError: managed.clearError,
    connectionState,
    currentPartial,
    partialHistory,
    finals,
    stats,
    error,
    config,
    audioSync,
    speakers,
    connect,
    disconnect,
    sendAudioData,
    clearPartials,
    clearFinals,
    clearAll,
    reset,
    startPending: managed.isPending,
    setConfig,
    updateAudioSync,
    updateFinalSegment,
    updateSpeaker,
    addSpeaker
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtimeContext = (): RealtimeContextType => {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error(
      'useRealtimeContext must be used within a RealtimeProvider'
    );
  }
  return context;
};
